import { useLayoutEffect, useState } from 'react'

import { logger, subscribeEventTarget } from '@subaio/utils'

// We need to declare our custom events for the typings for the event to match
declare global {
	interface WindowEventMap {
		updateLocalStorage: CustomEvent<{
			key: string
			value: any
		}>
	}
}

/**
 * Basic hook to get and set values in the local storage of a browser.
 * The hook will automatically update the state value of the local storage is updated with the same hook elsewhere,
 * and it will also update the state if the local storage is updated from another instance of the same origin.
 * @example
 * ```ts
 * export const Component = () => {
 * 	const [featureState, setFeatureState] = useLocalStorage<'stateOne' | 'stateTwo'>('featureName', 'stateOne')
 * 	console.log(featureState)
 * }
 * ```
 */
export const useLocalStorage = <T>(key: string, initialValue: T) => {
	const [storedValue, setStoredValue] = useState<T>(() => {
		// If window doesn't exist, we just return the initial value
		if (typeof window === 'undefined') return initialValue

		try {
			const item = window.localStorage.getItem(key)
			return item ? JSON.parse(item) : initialValue
		} catch (error) {
			logger.error(error)
			return initialValue
		}
	})

	const setValue = (value: T | ((val: T) => T)) => {
		try {
			const valueToStore = value instanceof Function ? value(storedValue) : value
			setStoredValue(valueToStore)
			// We create our own event to let other instances of the useLocalStorage() hook know that the value has changed
			const event = new CustomEvent('updateLocalStorage', { detail: { key, value: valueToStore } })
			window.dispatchEvent(event)
			if (typeof window !== 'undefined') window.localStorage.setItem(key, JSON.stringify(valueToStore))
		} catch (error) {
			logger.error(error)
		}
	}

	// We check if the value in local storage has changed by listening to our custom event and update the hook value
	// to ensure that other instances of the same key are in sync (e.g. using the hook in more than one component)
	useLayoutEffect(
		() =>
			subscribeEventTarget(window, 'updateLocalStorage', (event) => {
				if (event.detail.key === key) {
					setStoredValue(event.detail.value as T)
				}
			}),
		[key],
	)

	// Ensure that when the local storage value changes in other origins, update those as well
	useLayoutEffect(
		() =>
			subscribeEventTarget(window, 'storage', (event) => {
				try {
					if (event.key === key) {
						// If the local storage key is the same as the one we are using, we update the value
						const newValue = event.newValue ? JSON.parse(event.newValue) : null
						if (newValue !== null) setStoredValue(newValue)
					}
				} catch (error) {
					logger.error(error)
				}
			}),
		[key, initialValue],
	)

	return [storedValue, setValue] as const
}
